import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, ExternalLink, Loader2, Sparkles, Globe,
  Calendar, User, FileText, Rss, CheckCircle2, XCircle,
  AlertCircle, Clock, Edit3, X, Save, Cpu, GitCompare,
  Image as ImageIcon, Search, MinusCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';


const STORY_STATUS_CFG = {
  published:   { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',  label: 'Published' },
  draft:       { colour: 'bg-blue-50 text-blue-700 border-blue-200',           label: 'Draft' },
  review:      { colour: 'bg-amber-50 text-amber-700 border-amber-200',        label: 'Review' },
  approved:    { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',  label: 'Approved' },
  refused:     { colour: 'bg-red-50 text-red-600 border-red-200',              label: 'Refused' },
  failed:      { colour: 'bg-red-50 text-red-600 border-red-200',              label: 'Failed' },
  unpublished: { colour: 'bg-slate-100 text-slate-600 border-slate-300',       label: 'Unpublished' },
};

const PARSE_STATUS_CFG = {
  complete: { colour: 'text-emerald-600', icon: CheckCircle2, label: 'Complete' },
  partial:  { colour: 'text-amber-600',   icon: AlertCircle,  label: 'Partial' },
  pending:  { colour: 'text-slate-400',   icon: Clock,        label: 'Pending' },
  failed:   { colour: 'text-red-600',     icon: XCircle,      label: 'Failed' },
};

const CREATION_STATUS_CFG = {
  pending:        { colour: 'bg-slate-100 text-slate-600 border-slate-300',          label: 'Not generated', icon: Clock },
  complete:       { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',     label: 'Generated',     icon: CheckCircle2 },
  rewrite_failed: { colour: 'bg-amber-50 text-amber-700 border-amber-200',           label: 'Rewrite failed', icon: AlertCircle },
  failed:         { colour: 'bg-red-50 text-red-600 border-red-200',                 label: 'Failed',        icon: XCircle },
  in_progress:    { colour: 'bg-blue-50 text-blue-600 border-blue-200',              label: 'Generating',    icon: Loader2 },
};

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleString();
};

const fmtDuration = (ms) => {
  if (ms === null || ms === undefined) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const VALID_TABS = ['source', 'article', 'generated'];

const getLiveUrl = (output) => {
  if (!output?.publication_domain || !output?.story_slug) return null;
  const tag = output.story_tags?.[0]
    ? output.story_tags[0].toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${output.publication_domain}/${tag}/${output.story_slug}`;
};


// Empty-state messaging

const EMPTY_COPY = {
  not_available: { label: 'Not available',      Icon: MinusCircle, tone: 'text-slate-400' },
  could_not_get: { label: 'Could not retrieve', Icon: AlertCircle, tone: 'text-amber-600' },
  pending:       { label: 'Not yet fetched',    Icon: Clock,       tone: 'text-slate-400' },
};

const emptyReason = (item, field) => {
  if (field === 'body_text') {
    return item.parse_status ? 'could_not_get' : 'pending';
  }
  return 'not_available';
};

function EmptyState({ item, field, size = 'sm' }) {
  const { label, Icon, tone } = EMPTY_COPY[emptyReason(item, field)] || EMPTY_COPY.not_available;
  const text = size === 'xs' ? 'text-[11px]' : 'text-sm';
  return (
    <span className={`inline-flex items-center gap-1.5 italic ${text} ${tone}`}>
      <Icon size={size === 'xs' ? 11 : 12} className="shrink-0" /> {label}
    </span>
  );
}


function MediaLibraryPicker({ open, onClose, onSelect }) {
  const [assets, setAssets]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [tags, setTags]       = useState('');
  const [offset, setOffset]   = useState(0);
  const limit = 24;

  useEffect(() => {
    if (!open) return;
    fetchAssets();
  }, [open, search, tags, offset]);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (search) params.search = search;
      if (tags)   params.tags   = tags;
      const { data } = await api.get('/media/assets', { params });
      setAssets(data.assets || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900">Select image from library</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => { setOffset(0); setSearch(e.target.value); }}
              placeholder="Search filename, caption, alt text..."
              className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500"
            />
          </div>
          <input
            value={tags}
            onChange={e => { setOffset(0); setTags(e.target.value); }}
            placeholder="tags (comma sep)"
            className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 w-44"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : assets.length === 0 ? (
            <p className="text-center py-12 text-slate-400 text-sm">No images found.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {assets.map(a => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onSelect({
                    file_url:       a.file_url,
                    media_asset_id: a.id,
                    caption:        a.caption || '',
                    alt_text:       a.alt_text || '',
                    accreditation:  a.source_attribution || '',
                  })}
                  className="group text-left border border-slate-200 rounded-lg overflow-hidden hover:border-blue-400 transition-colors"
                >
                  <div className="aspect-video bg-slate-100">
                    <img
                      src={a.thumbnail_url || a.file_url}
                      alt={a.alt_text || a.filename}
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-1.5">
                    <p className="text-[11px] text-slate-700 truncate">{a.filename}</p>
                    {typeof a.usage_count === 'number' && (
                      <p className="text-[10px] text-slate-400">Used {a.usage_count}x</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {total > limit && (
          <div className="flex items-center justify-center gap-3 px-5 py-3 border-t border-slate-200">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
            >
              Previous
            </button>
            <span className="text-xs text-slate-400">
              Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
            </span>
            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


function OutputCard({ output, models, onSaved, autoOpen }) {
  const cfg     = STORY_STATUS_CFG[output.status] || STORY_STATUS_CFG.draft;
  const liveUrl = getLiveUrl(output);
  const modelLabel = output.ai_model_used
    ? (models.find(m => m.id === output.ai_model_used)?.label || output.ai_model_used)
    : null;
  const durationStr = fmtDuration(output.ai_eval_duration_ms);

  const [editing, setEditing]     = useState(false);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [form, setForm]           = useState({
    headline: '', lede: '', body: '',
    image: '', image_caption: '', image_accreditation: '',
    tagsInput: '',
  });

  const didAutoOpen = useRef(false);
  const isPublished = output.status === 'published';

  const openEditor = async () => {
    if (!output.story_id) return;
    setEditing(true);
    setLoading(true);
    try {
      const { data } = await api.get(`/feeds/stories/${output.story_id}`);
      setForm({
        headline:            data.headline || '',
        lede:                data.lede || '',
        body:                data.body || '',
        image:               data.image || '',
        image_caption:       data.image_caption || '',
        image_accreditation: data.image_accreditation || '',
        tagsInput:           Array.isArray(data.tags) ? data.tags.join(', ') : '',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load story');
      setEditing(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoOpen && !didAutoOpen.current && output.story_id && !editing) {
      didAutoOpen.current = true;
      openEditor();
    }

  }, [autoOpen]);

  const tagsArray = form.tagsInput
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

  const save = async () => {
    if (!form.headline.trim()) {
      toast.error('Headline cannot be empty');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/feeds/stories/${output.story_id}`, {
        headline:            form.headline,
        lede:                form.lede,
        body:                form.body,
        image:               form.image,
        image_caption:       form.image_caption,
        image_accreditation: form.image_accreditation,
        tags:                tagsArray.map(t => t.toLowerCase()),
      });
      toast.success(isPublished ? 'Live story updated' : 'Story updated');
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update story');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Globe size={13} className="text-slate-400 shrink-0" />
          <span className="text-sm text-slate-900 font-medium">{output.publication_name}</span>
          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.colour}`}>
            {cfg.label}
          </span>
          {modelLabel && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-purple-50 text-purple-700 border-purple-200 inline-flex items-center gap-1">
              <Cpu size={9} />
              {modelLabel}
            </span>
          )}
          {durationStr && (
            <span className="text-[10px] text-slate-400">{durationStr}</span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {output.story_id && !editing && (
            <button
              onClick={openEditor}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            >
              <Edit3 size={11} /> Edit
            </button>
          )}
          {isPublished && liveUrl && (
            <a
              href={liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
            >
              View live <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>

      {output.story_headline && !editing && (
        <p className="text-xs text-slate-500 mb-2 line-clamp-2">{output.story_headline}</p>
      )}

      {!editing && (
        <div className="flex items-center gap-3 text-[11px] text-slate-400 flex-wrap">
          <span>Created {fmtDate(output.created_at)}</span>
          {output.published_at && (
            <span className="text-emerald-600">Published {fmtDate(output.published_at)}</span>
          )}
        </div>
      )}

      {editing && (
        loading ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
            <Loader2 size={14} className="animate-spin" /> Loading story...
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Headline</label>
              <input
                value={form.headline}
                onChange={e => setForm(f => ({ ...f, headline: e.target.value }))}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm font-semibold focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Lede</label>
              <textarea
                value={form.lede}
                onChange={e => setForm(f => ({ ...f, lede: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Body</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                rows={12}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500 resize-y"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Image</label>
              <div className="flex items-start gap-3">
                {form.image ? (
                  <img
                    src={form.image}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="w-40 h-24 object-cover rounded-lg border border-slate-200 bg-slate-100 shrink-0"
                  />
                ) : (
                  <div className="w-40 h-24 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center text-slate-300 shrink-0">
                    <ImageIcon size={20} />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-blue-400 text-slate-700 rounded text-xs font-medium transition-colors"
                  >
                    <ImageIcon size={13} /> Replace image
                  </button>
                  {form.image && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, image: '' }))}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-slate-400 hover:text-red-500 rounded text-xs transition-colors"
                    >
                      <X size={13} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Image caption</label>
                <input
                  value={form.image_caption}
                  onChange={e => setForm(f => ({ ...f, image_caption: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Image accreditation</label>
                <input
                  value={form.image_accreditation}
                  onChange={e => setForm(f => ({ ...f, image_accreditation: e.target.value }))}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Tags</label>
              <input
                value={form.tagsInput}
                onChange={e => setForm(f => ({ ...f, tagsInput: e.target.value }))}
                placeholder="comma separated, e.g. politics, harrow"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 text-sm focus:outline-none focus:border-blue-500"
              />
              {tagsArray.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {tagsArray.map((t, i) => (
                    <span key={i} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                      {t.toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {isPublished && (
              <p className="text-[11px] text-amber-600 inline-flex items-start gap-1.5">
                <AlertCircle size={11} className="mt-0.5 shrink-0" />
                This story is live. Saving updates the published article directly — the public site may take a few minutes to reflect changes if caching is enabled.
              </p>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={save}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-medium transition-colors"
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-slate-400 hover:text-slate-700 disabled:opacity-50 text-xs transition-colors"
              >
                <X size={13} /> Cancel
              </button>
            </div>

            <MediaLibraryPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onSelect={(asset) => {
                setForm(f => ({
                  ...f,
                  image:               asset.file_url || f.image,
                  image_caption:       asset.caption || f.image_caption,
                  image_accreditation: asset.accreditation || f.image_accreditation,
                }));
                setPickerOpen(false);
              }}
            />
          </div>
        )
      )}
    </div>
  );
}


export default function FeedsItemDetailPage() {
  const { itemId } = useParams();
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return VALID_TABS.includes(t) ? t : 'article';
  });
  const [generating, setGenerating] = useState(false);

  const [models, setModels]                   = useState([]);
  const [selectedModelId, setSelectedModelId] = useState(null);

  const editStoryId = searchParams.get('edit');

  useEffect(() => { load(); }, [itemId]);
  useEffect(() => { loadModels(); }, []);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (VALID_TABS.includes(t) && t !== activeTab) {
      setActiveTab(t);
    }
  }, [searchParams]); 

  const changeTab = (id) => {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: result } = await api.get(`/feeds/items/${itemId}`);
      setData(result);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load item');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const { data: result } = await api.get('/feeds/models');
      setModels(result.models || []);
      setSelectedModelId(result.default_id || result.models?.[0]?.id || null);
    } catch (err) {
      console.warn('Failed to load model registry', err);
    }
  };

  const handleGenerate = async (modelIds) => {
    setGenerating(true);
    try {
      const payload = modelIds && modelIds.length > 0 ? { model_ids: modelIds } : {};
      const { data: result } = await api.post(`/feeds/items/${itemId}/generate-stories`, payload);
      const created    = result.stories_created || 0;
      const failed     = result.failed || 0;
      const modelsUsed = (result.models_used || []).length;

      if (created > 0 && failed === 0) {
        const modelLabel = modelsUsed > 1 ? ` across ${modelsUsed} models` : '';
        toast.success(`Generated ${created} ${created === 1 ? 'story' : 'stories'}${modelLabel}`);
      } else if (created > 0 && failed > 0) {
        toast.warning(`Generated ${created}, failed ${failed}`);
      } else if (failed > 0) {
        const firstError = result.results?.find(r => !r.success)?.error || 'Generation failed';
        toast.error(`Failed: ${firstError}`);
      }
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail?.rewrite_failures?.length > 0) {
        toast.error(`Rewrite validation failed: ${detail.rewrite_failures.join('; ')}`);
      } else {
        toast.error(detail || 'Generation failed');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'editor') {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">You do not have access to EventFlow.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
        <Loader2 size={16} className="animate-spin" /> Loading...
      </div>
    );
  }

  if (!data?.item) {
    return <div className="text-center py-16 text-slate-400 text-sm">Item not found.</div>;
  }

  const { item, outputs } = data;
  const creationCfg  = CREATION_STATUS_CFG[item.story_creation_status] || CREATION_STATUS_CFG.pending;
  const CreationIcon = creationCfg.icon;
  const parseCfg     = PARSE_STATUS_CFG[item.parse_status] || PARSE_STATUS_CFG.pending;
  const ParseIcon    = parseCfg.icon;

  const tabs = [
    { id: 'source',    label: 'Source',    icon: Rss },
    { id: 'article',   label: 'Article',   icon: FileText },
    { id: 'generated', label: 'Generated', icon: Sparkles, count: outputs.length },
  ];

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors mt-0.5 shrink-0"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
              {item.feed_source_name}
              {item.primary_organisation_name && ` · ${item.primary_organisation_name}`}
            </p>
            <h1 className="text-xl font-bold text-slate-900 leading-snug">
              {item.title}
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              First seen {fmtDate(item.first_seen_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-semibold border ${creationCfg.colour}`}>
            <CreationIcon size={10} className={item.story_creation_status === 'in_progress' ? 'animate-spin' : ''} />
            {creationCfg.label}
          </span>
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors"
          >
            Source <ExternalLink size={11} />
          </a>
        </div>
      </div>

      {/* Meta bar */}
      <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap text-[11px] text-slate-400 shadow-sm">
        <span className="inline-flex items-center gap-1">
          <ParseIcon size={11} className={parseCfg.colour} />
          <span>Parse: <span className={parseCfg.colour}>{parseCfg.label}</span></span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Calendar size={11} />
          {item.source_published_at
            ? fmtDate(item.source_published_at)
            : <EmptyState item={item} field="source_published_at" size="xs" />}
        </span>
        <span className="inline-flex items-center gap-1">
          <User size={11} />
          {item.author
            ? item.author
            : <EmptyState item={item} field="author" size="xs" />}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200 w-fit overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => changeTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Icon size={14} />
              {t.label}
              {t.count > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center ${
                  activeTab === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'
                }`}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Source tab */}
      {activeTab === 'source' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-4">Source Details</h2>
          <dl className="space-y-0">
            {[
              { label: 'Source name',    value: item.feed_source_name },
              { label: 'Organisation',   value: item.primary_organisation_name || '—' },
              { label: 'Parser type',    value: item.feed_parser_type || item.parser_used || '—' },
              { label: 'Source URL',     value: item.source_url,      link: item.source_url },
              { label: 'Canonical URL',  value: item.canonical_url || '—', link: item.canonical_url },
              { label: 'Source GUID',    value: item.source_guid || '—' },
              { label: 'URL hash',       value: item.url_hash || '—', mono: true },
              { label: 'First seen',     value: fmtDate(item.first_seen_at) },
              { label: 'Last seen',      value: fmtDate(item.last_seen_at) },
              { label: 'Fetched at',     value: fmtDate(item.fetched_at) },
              { label: 'HTTP status',    value: item.http_status_code || '—' },
              { label: 'Content length', value: item.content_length ? `${item.content_length.toLocaleString()} bytes` : '—' },
              { label: 'Fetch duration', value: item.fetch_duration_ms ? `${item.fetch_duration_ms} ms` : '—' },
              { label: 'Parse status',   value: item.parse_status || '—' },
              { label: 'Image status',   value: item.image_match_status || '—' },
              { label: 'Story status',   value: item.story_creation_status || '—' },
              { label: 'Publish status', value: item.publish_status || '—' },
            ].map(({ label, value, link, mono }) => (
              <div key={label} className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0">
                <dt className="text-xs text-slate-400 uppercase tracking-wider font-semibold shrink-0 pt-0.5 w-36">{label}</dt>
                <dd className="text-sm text-right break-all">
                  {link
                    ? (
                      <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-blue-600 hover:text-blue-700 hover:underline ${mono ? 'font-mono text-xs' : ''}`}
                      >
                        {value}
                      </a>
                    )
                    : <span className={mono ? 'font-mono text-xs text-slate-500' : 'text-slate-900'}>{value}</span>
                  }
                </dd>
              </div>
            ))}
          </dl>

          {item.parsing_warnings?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mb-2">Parse warnings</p>
              <ul className="space-y-1">
                {item.parsing_warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-600 flex items-start gap-2">
                    <AlertCircle size={11} className="mt-0.5 shrink-0" /> {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Article tab */}
      {activeTab === 'article' && (
        <div className="space-y-4">
          {item.source_image_url ? (
            <div className="max-w-lg">
              <img
                src={item.source_image_url}
                alt=""
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                className="w-full rounded-xl aspect-video object-cover bg-slate-100"
              />
            </div>
          ) : (
            <div className="max-w-lg">
              <div className="w-full aspect-video rounded-xl bg-slate-50 border border-dashed border-slate-200 flex items-center justify-center">
                <EmptyState item={item} field="source_image_url" />
              </div>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 shadow-sm">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 block">Title</label>
              <p className="text-slate-900 font-semibold leading-snug">{item.title}</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 block">Summary</label>
              {item.summary ? (
                <p className="text-slate-600 text-sm leading-relaxed">{item.summary}</p>
              ) : (
                <EmptyState item={item} field="summary" />
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Body text</label>
                {item.body_text && (
                  <span className="text-[11px] text-slate-400">
                    {item.body_text.length.toLocaleString()} chars · ~{Math.ceil(item.body_text.split(/\s+/).length / 200)} min read
                  </span>
                )}
              </div>
              {item.body_text ? (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200 max-h-96 overflow-y-auto">
                  <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{item.body_text}</p>
                </div>
              ) : (
                <EmptyState item={item} field="body_text" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generated tab */}
      {activeTab === 'generated' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-slate-400">
              {outputs.length} {outputs.length === 1 ? 'story' : 'stories'} generated
            </p>

            <div className="flex items-center gap-2 flex-wrap">
              {models.length > 0 && (
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg pl-3 pr-1 py-1">
                  <Cpu size={12} className="text-slate-400" />
                  <select
                    value={selectedModelId || ''}
                    onChange={e => setSelectedModelId(e.target.value)}
                    disabled={generating}
                    className="bg-transparent text-sm text-slate-900 focus:outline-none disabled:opacity-50 pr-2"
                  >
                    {models.map(m => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <button
                onClick={() => handleGenerate(selectedModelId ? [selectedModelId] : null)}
                disabled={generating}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {generating ? 'Generating...' : 'Generate'}
              </button>

              {models.length >= 2 && (
                <button
                  onClick={() => handleGenerate(models.map(m => m.id))}
                  disabled={generating}
                  className="flex items-center gap-2 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 text-purple-700 px-4 py-2 rounded-lg text-sm font-medium border border-purple-200 transition-colors"
                  title="Generate one story per model for side-by-side comparison"
                >
                  <GitCompare size={14} /> Compare all models
                </button>
              )}
            </div>
          </div>

          {outputs.length === 0 ? (
            <div className="text-center py-14 bg-white border-2 border-dashed border-slate-200 rounded-xl">
              <FileText size={28} className="text-slate-300 mx-auto mb-3" />
              <p className="text-slate-700 font-medium mb-1">No stories generated yet</p>
              <p className="text-slate-400 text-sm">
                Click Generate to fan this item out to all subscribing publications.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {outputs.map(o => (
                <OutputCard
                  key={o.id}
                  output={o}
                  models={models}
                  onSaved={load}
                  autoOpen={!!editStoryId && String(o.story_id) === String(editStoryId)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}